import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  StickyNote,
  Search,
  Edit,
  Trash2,
  Share2,
  Tag,
  User,
  Clock,
  Save,
  X,
  Users,
  Lock,
  ExternalLink
} from 'lucide-react';
import { TiptapEditor } from '../ui/TiptapEditor';
import { useQuery, useMutation, useApolloClient } from '@/apollo';
import { gql } from '@/apollo';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmModal } from '../../framework/modals/ConfirmModal';

// GraphQL Queries and Mutations
const GET_NOTES = gql`
  query GetNotes {
    notes {
      id
      title
      content
      isPublic
      tags
      createdAt
      updatedAt
      canEdit
      author {
        id
        username
        firstName
        lastName
      }
      sharedWith {
        id
        canEdit
        user {
          id
          username
          firstName
          lastName
        }
      }
    }
  }
`;

const SEARCH_NOTES = gql`
  query SearchNotes($query: String!) {
    searchNotes(query: $query) {
      id
      title
      content
      isPublic
      tags
      createdAt
      updatedAt
      canEdit
      author {
        id
        username
        firstName
        lastName
      }
    }
  }
`;

const CREATE_NOTE = gql`
  mutation CreateNote($input: CreateNoteInput!) {
    createNote(input: $input) {
      id
      title
      content
      isPublic
      tags
      createdAt
      author {
        id
        username
        firstName
        lastName
      }
    }
  }
`;

const UPDATE_NOTE = gql`
  mutation UpdateNote($id: ID!, $input: UpdateNoteInput!) {
    updateNote(id: $id, input: $input) {
      id
      title
      content
      isPublic
      tags
      updatedAt
      author {
        id
        username
        firstName
        lastName
      }
    }
  }
`;

const DELETE_NOTE = gql`
  mutation DeleteNote($id: ID!) {
    deleteNote(id: $id)
  }
`;

const SHARE_NOTE = gql`
  mutation ShareNote($noteId: ID!, $userId: ID!, $canEdit: Boolean!) {
    shareNote(noteId: $noteId, userId: $userId, canEdit: $canEdit) {
      id
      canEdit
      user {
        id
        username
        firstName
        lastName
      }
    }
  }
`;

const UNSHARE_NOTE = gql`
  mutation UnshareNote($noteId: ID!, $userId: ID!) {
    unshareNote(noteId: $noteId, userId: $userId)
  }
`;

const GET_USERS = gql`
  query GetUsers {
    users {
      id
      username
      firstName
      lastName
      email
    }
  }
`;

interface Note {
  id: string;
  title: string;
  content: string;
  isPublic: boolean;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
  author: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  sharedWith?: Array<{
    id: string;
    canEdit: boolean;
    user: {
      id: string;
      username: string;
      firstName?: string;
      lastName?: string;
    };
  }>;
}

export const NotesSystem: React.FC = () => {
  const { user } = useAuth();
  const apolloClient = useApolloClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'my' | 'shared' | 'public'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [shareSearchTerm, setShareSearchTerm] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingNote, setViewingNote] = useState<Note | null>(null);
  const [showScaledWindow, setShowScaledWindow] = useState(false);
  const [scaledNote] = useState<Note | null>(null); // setScaledNote reserved for future use
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; noteId: string | null }>({
    isOpen: false,
    noteId: null
  });

  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isPublic: false,
    tags: [] as string[],
    newTag: ''
  });

  // Fetch notes
  const { data: notesData, loading: isLoading, error, refetch } = useQuery(GET_NOTES, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });
  
  const notes = notesData?.notes || [];

  // Search notes
  const { data: searchNotesData } = useQuery(SEARCH_NOTES, {
    variables: { query: searchTerm },
    skip: !searchTerm.trim()
  });
  
  const searchNotes = searchNotesData?.searchNotes || [];

  // Fetch users for sharing
  const { data: usersData } = useQuery(GET_USERS, {
    fetchPolicy: 'network-only',
    skip: !showShareModal
  });
  
  const users = usersData?.users || [];

  // Filter users for search suggestions
  const shareSearchSuggestions = users
    ?.filter((u: { id: string; username: string; firstName?: string; lastName?: string; email: string }) => {
      // Don't show current user or already shared users
      if (u.id === user?.id) return false;
      if (selectedNote?.sharedWith?.some(share => share.user.id === u.id)) return false;
      
      // Filter by search term
      if (shareSearchTerm.trim()) {
        const searchLower = shareSearchTerm.toLowerCase();
        return (
          u.username.toLowerCase().includes(searchLower) ||
          u.firstName?.toLowerCase().includes(searchLower) ||
          u.lastName?.toLowerCase().includes(searchLower) ||
          u.email.toLowerCase().includes(searchLower)
        );
      }
      return false; // Only show suggestions when typing
    })
    .slice(0, 5); // Limit to 5 suggestions

  // Mutations
  const [createNoteMutation, { loading: creatingNote }] = useMutation(CREATE_NOTE, {
    onCompleted: async () => {
      setShowCreateModal(false);
      resetForm();
      // Force a fresh refetch of notes
      await refetch();
    },
    errorPolicy: 'all'
  });

  const [updateNoteMutation, { loading: updatingNote }] = useMutation(UPDATE_NOTE, {
    onCompleted: async () => {
      setEditingNote(null);
      setShowCreateModal(false);
      resetForm();
      // Force a fresh refetch of notes
      await refetch();
    },
    errorPolicy: 'all'
  });

  const [deleteNoteMutation] = useMutation(DELETE_NOTE, {
    onCompleted: async () => {
      // Force a fresh refetch of notes
      await refetch();
    },
    errorPolicy: 'all'
  });

  const [shareNoteMutation, { loading: sharingNote }] = useMutation(SHARE_NOTE, {
    onCompleted: async () => {
      // Refresh notes and update selected note
      await refetch();
      if (selectedNote) {
        const { data } = await apolloClient.query({
          query: GET_NOTES,
          fetchPolicy: 'network-only'
        });
        const updatedNote = data.notes.find((note: Note) => note.id === selectedNote.id);
        if (updatedNote) {
          setSelectedNote(updatedNote);
        }
      }
    },
    errorPolicy: 'all'
  });

  const [unshareNoteMutation] = useMutation(UNSHARE_NOTE, {
    onCompleted: async () => {
      // Refresh notes and update selected note
      await refetch();
      if (selectedNote) {
        const { data }: { data: { notes: Note[] } } = await apolloClient.query({
          query: GET_NOTES,
          fetchPolicy: 'network-only'
        });
        const updatedNote = data.notes.find((note: Note) => note.id === selectedNote.id);
        if (updatedNote) {
          setSelectedNote(updatedNote);
        }
      }
    },
    errorPolicy: 'all'
  });

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      isPublic: false,
      tags: [],
      newTag: ''
    });
  };

  const handleSubmit = () => {
    const input = {
      title: formData.title,
      content: formData.content,
      isPublic: formData.isPublic,
      tags: formData.tags
    };

    if (editingNote) {
      updateNoteMutation({ variables: { id: editingNote.id, input } });
    } else {
      createNoteMutation({ variables: { input } });
    }
  };

  const handleEdit = (note: Note) => {
    setEditingNote(note);
    setFormData({
      title: note.title,
      content: note.content,
      isPublic: note.isPublic,
      tags: note.tags,
      newTag: ''
    });
    setShowCreateModal(true);
  };

  const handleDelete = (id: string) => {
    setDeleteConfirmModal({ isOpen: true, noteId: id });
  };

  const addTag = () => {
    if (formData.newTag.trim() && !formData.tags.includes(formData.newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, prev.newTag.trim()],
        newTag: ''
      }));
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleShareNote = (userId: string, canEdit: boolean) => {
    if (selectedNote) {
      shareNoteMutation({ variables: { noteId: selectedNote.id, userId, canEdit } });
    }
  };

  const handleUnshareNote = (userId: string) => {
    if (selectedNote) {
      unshareNoteMutation({ variables: { noteId: selectedNote.id, userId } });
    }
  };

  const openShareModal = (note: Note) => {
    setSelectedNote(note);
    setShareSearchTerm('');
    setShowShareModal(true);
  };

  const openViewModal = (note: Note) => {
    setViewingNote(note);
    setShowViewModal(true);
  };

  // const openScaledWindow = (note: Note) => { // Reserved for future use
  //   setScaledNote(note);
  //   setShowScaledWindow(true);
  // };

  const getUserDisplayName = (user: { username: string; firstName?: string; lastName?: string }) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username;
  };



  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getDisplayNotes = () => {
    const baseNotes: Note[] = (searchTerm.trim() && searchNotes.length > 0 ? searchNotes : notes || []) as Note[];
    
    switch (filterType) {
      case 'my':
        return baseNotes.filter((note: Note) => note.author.id === user?.id);
      case 'shared':
        return baseNotes.filter((note: Note) => note.author.id !== user?.id);
      case 'public':
        return baseNotes.filter((note: Note) => note.isPublic);
      default:
        return baseNotes;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-600">Loading notes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <StickyNote className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Notes</h3>
        <p className="text-gray-600">Failed to load notes. Please try again.</p>
      </div>
    );
  }

  const displayNotes = getDisplayNotes();

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <StickyNote className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Notes
            </h1>
            <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>
              Create, search, and share notes with your team.
            </p>
          </div>
        </div>
        
        {/* Search and Filter Bar */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
            <input
              type="text"
              placeholder="Search notes by title, content, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border transition-all"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)'
              }}
            />
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'white'
            }}
          >
            + New Note
          </button>
          <button
            onClick={() => setFilterType('all')}
            className="px-4 py-3 rounded-lg border transition-all"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-secondary)'
            }}
          >
            All Notes
          </button>
        </div>
      </div>

      {/* Notes Grid */}
      {isLoading ? (
        <div className="col-span-full text-center py-12 rounded-lg border" style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)'
        }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>Loading notes...</span>
        </div>
      ) : error ? (
        <div className="col-span-full text-center py-12 rounded-lg border" style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)'
        }}>
          <span style={{ color: 'var(--color-error)' }}>Error loading notes: {String(error)}</span>
        </div>
      ) : displayNotes.length === 0 ? (
        <div className="col-span-full text-center py-12 rounded-lg border" style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)'
        }}>
          <StickyNote className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>No notes</h3>
          <p style={{ color: 'var(--color-text-secondary)' }}>There are no notes to display.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayNotes.map((note: Note) => (
            <motion.div
              key={note.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="group rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)'
              }}
              onClick={() => openViewModal(note)}
            >
              {/* Note Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {note.isPublic ? (
                    <ExternalLink className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                  ) : (
                    <Lock className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                  )}
                  <span className="text-sm font-medium uppercase" style={{ color: 'var(--color-text-secondary)' }}>
                    {note.isPublic ? 'Public' : 'Private'}
                  </span>
                </div>
                {note.canEdit && (
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(note);
                      }}
                      className="p-1 rounded transition-colors"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openShareModal(note);
                      }}
                      className="p-1 rounded transition-colors"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(note.id);
                      }}
                      className="p-1 rounded transition-colors"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Note Content */}
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                {note.title}
              </h3>
              <div 
                className="text-sm mb-3 line-clamp-3" 
                style={{ color: 'var(--color-text-secondary)' }}
                dangerouslySetInnerHTML={{ 
                  __html: note.content.replace(/<[^>]*>/g, '') 
                }}
              />

              {/* Note Footer */}
              <div className="flex items-center justify-between text-sm" style={{ color: 'var(--color-text-muted)' }}>
                <div className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>{note.author.firstName && note.author.lastName 
                    ? `${note.author.firstName} ${note.author.lastName}`
                    : note.author.username
                  }</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{formatDate(note.updatedAt)}</span>
                </div>
              </div>

              {/* Tags */}
              {note.tags && note.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {note.tags.slice(0, 3).map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs font-medium rounded-full"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)',
                        color: 'var(--color-text-secondary)'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                  {note.tags.length > 3 && (
                    <span
                      className="px-2 py-1 text-xs font-medium rounded-full"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)',
                        color: 'var(--color-text-secondary)'
                      }}
                    >
                      +{note.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto dark-scrollbar"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {editingNote ? 'Edit Note' : 'Create New Note'}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="hover:opacity-70 transition-opacity"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-all"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)'
                      }}
                      placeholder="Enter note title..."
                    />
                  </div>

                  {/* Tiptap WYSIWYG Editor */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      Content
                    </label>
                    <TiptapEditor
                      content={formData.content}
                      onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                      placeholder="Start writing your note..."
                      className="focus-within:ring-2 transition-all"
                    />
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-1 text-sm rounded-full"
                          style={{
                            backgroundColor: 'var(--color-bg-tertiary)',
                            color: 'var(--color-text-secondary)'
                          }}
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 hover:opacity-70 transition-opacity"
                            style={{ color: 'var(--color-accent)' }}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={formData.newTag}
                        onChange={(e) => setFormData(prev => ({ ...prev, newTag: e.target.value }))}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 transition-all"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)'
                        }}
                        placeholder="Add a tag..."
                      />
                      <button
                        type="button"
                        onClick={addTag}
                        className="px-3 py-2 rounded-lg transition-colors"
                        style={{
                          backgroundColor: 'var(--color-bg-tertiary)',
                          color: 'var(--color-text-secondary)'
                        }}
                      >
                        <Tag className="w-4 h-4" />
                      </button>
                    </div>
                  </div>


                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-tertiary)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-secondary)'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!formData.title.trim() || creatingNote || updatingNote}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    style={{
                      backgroundColor: 'var(--color-accent)',
                      color: 'white'
                    }}
                  >
                    {(creatingNote || updatingNote) ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {editingNote ? 'Update' : 'Create'} Note
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scaled Down Note Window */}
      <AnimatePresence>
        {showScaledWindow && scaledNote && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: 50, y: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: 50, y: 50 }}
            className="fixed top-20 right-6 w-80 max-h-96 rounded-lg shadow-2xl border z-50 overflow-hidden"
            style={{
              resize: 'both',
              minWidth: '300px',
              minHeight: '200px',
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-xl)'
            }}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              borderColor: 'var(--color-border)'
            }}>
              <h4 className="text-sm font-semibold truncate flex-1 mr-2" style={{ color: 'var(--color-text-primary)' }}>
                {scaledNote.title}
              </h4>
              <div className="flex items-center space-x-2">
                {scaledNote.canEdit && (
                  <button
                    onClick={() => {
                      setShowScaledWindow(false);
                      handleEdit(scaledNote);
                    }}
                    className="p-1 rounded transition-colors hover:bg-opacity-80"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowScaledWindow(false);
                    openViewModal(scaledNote);
                  }}
                  className="p-1 rounded transition-colors hover:bg-opacity-80"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <ExternalLink className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setShowScaledWindow(false)}
                  className="p-1 rounded transition-colors hover:bg-opacity-80"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-64 dark-scrollbar">
              <div 
                className="text-sm leading-relaxed"
                style={{ color: 'var(--color-text-secondary)' }}
                dangerouslySetInnerHTML={{ 
                  __html: scaledNote.content.replace(/<[^>]*>/g, '') 
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Note Modal */}
      <AnimatePresence>
        {showViewModal && viewingNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto dark-scrollbar"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
            >
              {/* Header */}
              <div className="p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                      {viewingNote.title}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{viewingNote.author.firstName && viewingNote.author.lastName
                          ? `${viewingNote.author.firstName} ${viewingNote.author.lastName}`
                          : viewingNote.author.username}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{formatDate(viewingNote.updatedAt)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {viewingNote.isPublic ? (
                          <ExternalLink className="w-4 h-4" />
                        ) : (
                          <Lock className="w-4 h-4" />
                        )}
                        <span>{viewingNote.isPublic ? 'Public' : 'Private'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {viewingNote.canEdit && (
                      <button
                        onClick={() => {
                          setShowViewModal(false);
                          handleEdit(viewingNote);
                        }}
                        className="px-4 py-2 rounded-lg font-medium transition-all hover:scale-105"
                        style={{
                          backgroundColor: 'var(--color-accent)',
                          color: 'white'
                        }}
                      >
                        Edit Note
                      </button>
                    )}
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="p-2 rounded-lg transition-colors hover:bg-opacity-80"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div 
                  className="prose max-w-none"
                  style={{ color: 'var(--color-text-secondary)' }}
                  dangerouslySetInnerHTML={{ __html: viewingNote.content }}
                />
                
                {/* Tags */}
                {viewingNote.tags.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {viewingNote.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-sm font-medium rounded-full"
                          style={{
                            backgroundColor: 'var(--color-bg-tertiary)',
                            color: 'var(--color-text-secondary)'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t flex justify-end" style={{ borderColor: 'var(--color-border)' }}>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: 'var(--color-bg-tertiary)',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && selectedNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto dark-scrollbar"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Share Note</h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>"{selectedNote.title}"</p>
                  </div>
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="hover:opacity-70 transition-opacity"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Current Shares */}
                {selectedNote.sharedWith && selectedNote.sharedWith.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>Currently Shared With</h4>
                    <div className="space-y-2">
                      {selectedNote.sharedWith.map((share) => (
                        <div
                          key={share.id}
                          className="flex items-center justify-between p-3 rounded-lg"
                          style={{
                            backgroundColor: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border)'
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: 'var(--color-accent)',
                                color: 'white'
                              }}
                            >
                              <span className="text-sm font-medium">
                                {(getUserDisplayName(share.user)[0] || 'U').toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                                {getUserDisplayName(share.user)}
                              </p>
                              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>@{share.user.username}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span 
                              className="px-2 py-1 text-xs font-medium rounded-full"
                              style={{
                                backgroundColor: share.canEdit ? 'var(--color-success)' : 'var(--color-bg-tertiary)',
                                color: share.canEdit ? 'white' : 'var(--color-text-secondary)'
                              }}
                            >
                              {share.canEdit ? 'Can Edit' : 'View Only'}
                            </span>
                            <button
                              onClick={() => handleUnshareNote(share.user.id)}
                              className="hover:opacity-70 transition-opacity"
                              style={{ color: 'var(--color-error)' }}
                              title="Remove access"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add New Share */}
                <div>
                  <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>Share with Others</h4>
                  
                  {/* Search Users */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                      <input
                        type="text"
                        placeholder="Search users by name or username..."
                        value={shareSearchTerm}
                        onChange={(e) => setShareSearchTerm(e.target.value)}
                        onBlur={() => setTimeout(() => setShareSearchTerm(''), 150)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 transition-all"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                          '--tw-ring-color': 'var(--color-border-focus)'
                        } as any}
                      />
                      
                      {/* Autofill Suggestions */}
                      {shareSearchTerm && shareSearchSuggestions && shareSearchSuggestions.length > 0 && (
                        <div 
                          className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto dark-scrollbar"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border)',
                            boxShadow: 'var(--shadow-lg)'
                          }}
                        >
                          {shareSearchSuggestions.map((suggestionUser: { id: string; username: string; firstName?: string; lastName?: string }) => (
                            <button
                              key={suggestionUser.id}
                              onClick={() => {
                                setShareSearchTerm(getUserDisplayName(suggestionUser));
                                // Hide autofill menu by clearing search term
                                setTimeout(() => setShareSearchTerm(''), 100);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-opacity-50 transition-colors border-b last:border-b-0"
                              style={{
                                backgroundColor: 'var(--color-bg-primary)',
                                color: 'var(--color-text-primary)',
                                borderColor: 'var(--color-border)',
                                '--tw-hover-bg-opacity': '0.5'
                              } as React.CSSProperties}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                              }}
                            >
                              <div className="flex items-center space-x-3">
                                <div 
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs"
                                  style={{
                                    backgroundColor: 'var(--color-accent)',
                                    color: 'white'
                                  }}
                                >
                                  {(getUserDisplayName(suggestionUser)[0] || 'U').toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{suggestionUser.firstName && suggestionUser.lastName ? `${suggestionUser.firstName} ${suggestionUser.lastName}` : suggestionUser.username}</p>
                                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>@{suggestionUser.username}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                          
                          {/* No results message */}
                          {shareSearchTerm && shareSearchSuggestions && shareSearchSuggestions.length === 0 && (
                            <div className="px-4 py-3 text-center" style={{ color: 'var(--color-text-muted)' }}>
                              <p className="text-sm">No users found matching your search.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* User List */}
                  <div className="max-h-60 overflow-y-auto dark-scrollbar">
                    {users
                      ?.filter((u: { id: string; username: string; firstName?: string; lastName?: string; email: string }) => {
                        // Don't show current user or already shared users
                        if (u.id === user?.id) return false;
                        if (selectedNote.sharedWith?.some(share => share.user.id === u.id)) return false;
                        
                        // Filter by search term
                        if (shareSearchTerm.trim()) {
                          const searchLower = shareSearchTerm.toLowerCase();
                          return (
                            u.username.toLowerCase().includes(searchLower) ||
                            u.firstName?.toLowerCase().includes(searchLower) ||
                            u.lastName?.toLowerCase().includes(searchLower) ||
                            u.email.toLowerCase().includes(searchLower)
                          );
                        }
                        return true;
                      })
                      .map((targetUser: { id: string; username: string; firstName?: string; lastName?: string }) => (
                        <div
                          key={targetUser.id}
                          className="flex items-center justify-between p-3 hover:bg-opacity-80 rounded-lg transition-colors"
                          style={{
                            backgroundColor: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: 'var(--color-bg-tertiary)',
                                color: 'var(--color-text-secondary)'
                              }}
                            >
                              <span className="text-sm font-medium">
                                {(getUserDisplayName(targetUser)[0] || 'U').toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                                {getUserDisplayName(targetUser)}
                              </p>
                              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>@{targetUser.username}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleShareNote(targetUser.id, false)}
                              disabled={sharingNote}
                              className="px-3 py-1 text-xs font-medium rounded-full disabled:opacity-50 transition-colors"
                              style={{
                                backgroundColor: 'var(--color-bg-tertiary)',
                                color: 'var(--color-text-secondary)'
                              }}
                              onMouseEnter={(e) => {
                                if (!sharingNote) {
                                  e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                                  e.currentTarget.style.color = 'var(--color-text-primary)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!sharingNote) {
                                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                                }
                              }}
                            >
                              View Only
                            </button>
                            <button
                              onClick={() => handleShareNote(targetUser.id, true)}
                              disabled={sharingNote}
                              className="px-3 py-1 text-xs font-medium rounded-full disabled:opacity-50 transition-colors"
                              style={{
                                backgroundColor: 'var(--color-success)',
                                color: 'white'
                              }}
                              onMouseEnter={(e) => {
                                if (!sharingNote) {
                                  e.currentTarget.style.opacity = '0.8';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!sharingNote) {
                                  e.currentTarget.style.opacity = '1';
                                }
                              }}
                            >
                              Can Edit
                            </button>
                          </div>
                        </div>
                      ))}
                    
                    {users && shareSearchTerm.trim() && users.filter((u: { id: string; username: string; firstName?: string; lastName?: string; email: string }) => {
                      if (u.id === user?.id) return false;
                      if (selectedNote.sharedWith?.some(share => share.user.id === u.id)) return false;
                      const searchLower = shareSearchTerm.toLowerCase();
                      return (
                        u.username.toLowerCase().includes(searchLower) ||
                        u.firstName?.toLowerCase().includes(searchLower) ||
                        u.lastName?.toLowerCase().includes(searchLower) ||
                        u.email.toLowerCase().includes(searchLower)
                      );
                    }).length === 0 && (
                      <div className="text-center py-6" style={{ color: 'var(--color-text-muted)' }}>
                        <Users className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-muted)' }} />
                        <p className="text-sm">No users found matching your search.</p>
                      </div>
                    )}

                    {users && !shareSearchTerm.trim() && users.filter((u: { id: string; username: string; firstName?: string; lastName?: string }) => {
                      if (u.id === user?.id) return false;
                      if (selectedNote.sharedWith?.some(share => share.user.id === u.id)) return false;
                      return true;
                    }).length === 0 && (
                      <div className="text-center py-6" style={{ color: 'var(--color-text-muted)' }}>
                        <Users className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-muted)' }} />
                        <p className="text-sm">No other users available to share with.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, noteId: null })}
        onConfirm={async () => {
          if (deleteConfirmModal.noteId) {
            await deleteNoteMutation({ variables: { id: deleteConfirmModal.noteId } });
            setDeleteConfirmModal({ isOpen: false, noteId: null });
          }
        }}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        type="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};
